import { useEffect, useState } from "react";
import {
  IoEye,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoPrint,
  IoDownload,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useOrders } from "./useHook";
import { useOrders as useInvoiceOrders } from "./Invoice.jsx/useHook";
import Loader from "../../Utils/loader";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";
import { FiFilter, FiPlus, FiRotateCcw, FiSearch } from "react-icons/fi";

/** Loose email check so admins can search orders by customer email via the `email` query param. */
function looksLikeEmail(value) {
  const v = value.trim();
  if (!v.includes("@")) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function Order() {
  const { getORders, updateOrderStatus } = useOrders();
  const { getOrderByOrderId } = useInvoiceOrders();
  const navigate = useNavigate();

  const [searchName, setSearchName] = useState("");
  const [searchOrderId, setSearchOrderId] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 9,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(9);

  // Invoice print state
  const [invoiceOrderData, setInvoiceOrderData] = useState(null);

  useEffect(() => {
    fetchOrderData();
  }, [offset]);

  const fetchOrderData = async () => {
    try {
      setIsLoading(true);
      const response = await getORders(setOrderData, {}, offset, limit);
      setOrderData(response.ordersData);
      setFilteredData(response.ordersData);

      // Set pagination data if available
      if (response.pagination) {
        setPagination({
          totalRecords: response.pagination.totalRecords,
          totalPages: response.pagination.totalPages,
          currentPage: response.pagination.currentPage,
          perPage: response.pagination.perPage,
        });
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      // Only include parameters that have values
      const params = {};
      const nameOrEmailQuery = searchName.trim();
      if (nameOrEmailQuery) {
        if (looksLikeEmail(nameOrEmailQuery)) {
          params.email = nameOrEmailQuery;
        } else {
          params.name = nameOrEmailQuery;
        }
      }
      if (searchOrderId.trim()) {
        params.orderId = searchOrderId.trim();
      }
      if (searchStatus.trim()) {
        params.status = searchStatus.trim();
      }
      const response = await getORders(setOrderData, params, offset, limit);
      setFilteredData(response.ordersData);

      // Set pagination data if available
      if (response.pagination) {
        setPagination({
          totalRecords: response.pagination.totalRecords,
          totalPages: response.pagination.totalPages,
          currentPage: response.pagination.currentPage,
          perPage: response.pagination.perPage,
        });
      }
    } catch (error) {
      console.error("Error performing search:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handlePageView = (orderId) => {
    navigate(`/orders/invoice`, { state: { orderId } });
  };

  const handlePrint = async (orderId) => {
    try {
      // Fetch invoice data and set it
      await getOrderByOrderId(orderId, setInvoiceOrderData);

      // Small delay to ensure DOM is updated, then trigger print
      setTimeout(() => {
        window.print();
      }, 500);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load invoice data. Please try again.",
      });
    }
  };

  const handleVerifyPaymentPageView = () => {
    navigate(`/orders/verify-payment`);
  };

  const handleDownloadScreenshot = (screenshotUrl, orderId) => {
    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = screenshotUrl;
      link.download = `payment-screenshot-${orderId}.png`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading screenshot:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Failed to download screenshot. Please try again.",
      });
    }
  };

  const handleReset = async () => {
    setSearchName("");
    setSearchOrderId("");
    setSearchStatus("");
    setOffset(0);
    // Explicitly fetch data after reset
    try {
      setIsLoading(true);
      const response = await getORders(setOrderData, {}, 0, limit);
      setOrderData(response.ordersData);
      setFilteredData(response.ordersData);

      // Set pagination data if available
      if (response.pagination) {
        setPagination({
          totalRecords: response.pagination.totalRecords,
          totalPages: response.pagination.totalPages,
          currentPage: response.pagination.currentPage,
          perPage: response.pagination.perPage,
        });
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, status) => {
    const statusText = status === "approved" ? "approve" : "reject";
    const statusCapitalized = status === "approved" ? "Approve" : "Reject";

    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `Do you want to ${statusText} order ${orderId}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: status === "approved" ? "#10b981" : "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: `Yes, ${statusCapitalized} it!`,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        setIsLoading(true);
        await updateOrderStatus(orderId, status);

        // Show success message
        Swal.fire({
          icon: "success",
          title: `Order ${statusCapitalized}d`,
          text: `Order ${orderId} has been successfully ${statusText}ed.`,
          timer: 2000,
          showConfirmButton: false,
        });

        // Refresh the order data after status update
        await fetchOrderData();
      } catch (error) {
        console.error("Error updating order status:", error);
        Swal.fire({
          icon: "error",
          title: "Failed to Update Status",
          text: "An error occurred while updating the order status. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isLoading) {
    return <Loader message="Loading orders..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Manage Orders</h2>

        <button
          onClick={handleVerifyPaymentPageView}
          className="btn btn-primary h-[44px]"
        >
          {/* <FiPlus className="text-white" /> */}
          Verify Payments
        </button>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="search-input"
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search by Order ID"
            value={searchOrderId}
            onChange={(e) => setSearchOrderId(e.target.value)}
            className="search-input"
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          value={searchStatus}
          onChange={(e) => setSearchStatus(e.target.value)}
          className="status-select"
        >
          <option value="">Status</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>

        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No</th>
                <th className="table-th">User Name</th>
                <th className="table-th">No. of Items</th>
                <th className="table-th">Order ID</th>
                <th className="table-th">Status</th>
                <th className="table-th">Payment Screenshot</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData?.length > 0 ? (
                filteredData.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td">
                      {row.user
                        ? `${row.user.firstName} ${row.user.lastName}`
                        : "User info not available"}
                    </td>
                    <td className="table-td">{row.totalItemsPurchased}</td>
                    <td className="table-td">{row.orderId}</td>
                    <td className="table-td">
                      <div className="flex items-center gap-2">
                        <span className="capitalize">{row.status}</span>
                        {row.status === "pending" && (
                          <div className="flex gap-2 ml-2">
                            <button
                              onClick={() =>
                                handleStatusUpdate(row.orderId, "approved")
                              }
                              className="text-green-500 hover:text-green-600 transition-colors"
                              title="Approve Order"
                            >
                              <IoCheckmarkCircle size={24} />
                            </button>
                            <button
                              onClick={() =>
                                handleStatusUpdate(row.orderId, "rejected")
                              }
                              className="text-red-500 hover:text-red-600 transition-colors"
                              title="Reject Order"
                            >
                              <IoCloseCircle size={24} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="table-td">
                      {row.paymentScreenshots &&
                      row.paymentScreenshots.length > 0 ? (
                        <button
                          onClick={() =>
                            handleDownloadScreenshot(
                              row.paymentScreenshots[0].screenShot,
                              row.orderId,
                            )
                          }
                          className="bg-purple-500 text-white px-4 py-2 rounded shadow hover:bg-purple-600 flex items-center gap-2"
                          title="Download Payment Screenshot"
                        >
                          <IoDownload />
                          <span className="hidden sm:inline">Download</span>
                        </button>
                      ) : (
                        <span className="text-gray-500 italic text-sm">
                          Not uploaded yet
                        </span>
                      )}
                    </td>

                    <td className="table-td flex space-x-2">
                      <button
                        className="btn btn-view"
                        onClick={() => handlePageView(row.orderId)}
                      >
                        <IoEye className="text-white" /> View
                      </button>

                      <button
                        className="btn btn-action"
                        onClick={() => handlePrint(row.orderId)}
                        title="Print Invoice"
                      >
                        <IoPrint className="text-white" /> Print
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="table-td text-center text-gray-500 italic"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Component */}
      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={pagination.totalRecords}
            onPageChange={handlePageChange}
            className="mt-4"
          />
        </div>
      )}

      {/* Hidden Invoice Print Container */}
      <style>{`
        .invoice-print-container {
          position: fixed;
          left: -9999px;
          top: -9999px;
          visibility: hidden;
        }
        .invoice-table-wrapper {
          overflow-x: auto;
        }
        @media print {
          @page {
            margin: 15mm;
            size: A4;
          }
          body * {
            visibility: hidden;
          }
          .invoice-print-container, .invoice-print-container * {
            visibility: visible;
          }
          .invoice-print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
            background: white;
          }
          .invoice-print-container .bg-gray-50 {
            background: white !important;
          }
          .no-print {
            display: none !important;
          }
          table {
            page-break-inside: auto;
            width: 100%;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          .overflow-x-auto {
            overflow: visible !important;
          }
          .invoice-print-container {
            overflow: visible !important;
          }
        }
      `}</style>
      {invoiceOrderData && (
        <div className="invoice-print-container bg-gray-50 p-10">
          <div className="bg-white p-8 shadow-md rounded-lg mx-auto">
            <div className="flex justify-between items-center border-b pb-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-green-700">INVOICE</h1>
                <p className="text-gray-500">{invoiceOrderData?.orderId}</p>
              </div>
            </div>
            <div>
              <h1 className="text-4xl font-bold text-green-700">GharPlans</h1>
            </div>
            <div className="flex justify-between mb-6">
              <div>
                <h2 className="font-semibold text-lg">Bill From:</h2>
                <p className="text-gray-700">
                  Address: 469 G1 Johar Town Lahore
                </p>
                <p className="text-gray-700">Phone: +92 339 5111199</p>
                <p className="text-gray-500">Email: info@gharplans.com</p>
              </div>
              <div>
                <h2 className="font-semibold text-lg">Bill To:</h2>
                <p className="text-gray-700">
                  Name: {invoiceOrderData?.user?.firstName}{" "}
                  {invoiceOrderData?.user?.lastName}
                </p>
                <p className="text-gray-500">
                  Email: {invoiceOrderData?.user?.email}
                </p>
                {invoiceOrderData?.userBillingAddress && (
                  <>
                    <p className="text-gray-700 mt-2">
                      Address:{" "}
                      {invoiceOrderData.userBillingAddress.streetAddress}
                    </p>
                    <p className="text-gray-700">
                      {invoiceOrderData.userBillingAddress.city},{" "}
                      {invoiceOrderData.userBillingAddress.postalCode}
                    </p>
                    {invoiceOrderData.userBillingAddress.contactNo && (
                      <p className="text-gray-700">
                        Phone: {invoiceOrderData.userBillingAddress.contactNo}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div>
                <h2 className="font-semibold text-lg">Date:</h2>
                {invoiceOrderData?.createdAt && (
                  <>
                    <p className="text-gray-500">
                      {new Date(invoiceOrderData.createdAt)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, "-")}
                    </p>
                    <p className="text-gray-500">
                      {new Date(invoiceOrderData.createdAt).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )}
                    </p>
                  </>
                )}
                <div className="mt-3">
                  <p className="text-gray-700">
                    <span className="font-semibold">Payment Method:</span>{" "}
                    {invoiceOrderData?.paymentType === "Make Transfer"
                      ? "Bank Transferred"
                      : invoiceOrderData?.paymentType === "Pay Now"
                        ? "Credit/Debit Card"
                        : invoiceOrderData?.paymentType || "N/A"}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Delivery Mode:</span>{" "}
                    {invoiceOrderData?.deliveryType?.deliveryType || "N/A"}
                  </p>
                  {invoiceOrderData?.totalWeight && (
                    <p className="text-gray-700">
                      <span className="font-semibold">Weight:</span>{" "}
                      {invoiceOrderData.totalWeight}{" "}
                      {invoiceOrderData?.unit || "kg"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="invoice-table-wrapper overflow-x-auto print:overflow-visible">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left w-16">Sr. No</th>
                    <th className="border px-4 py-2 text-left w-2/5">
                      Product Name
                    </th>
                    <th className="border px-4 py-2 text-left w-20">
                      Quantity
                    </th>
                    <th className="border px-4 py-2 text-left w-24">Price</th>
                    <th className="border px-4 py-2 text-left w-24">
                      Discount
                    </th>
                    <th className="border px-4 py-2 text-left w-24">Rate</th>
                    <th className="border px-4 py-2 text-left w-24">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoiceOrderData?.productInfo || []).map(
                    (product, index) => {
                      const originalPrice =
                        parseFloat(product.originalPrice) || 0;
                      const discountedPrice =
                        parseFloat(product.discountedPrice) || originalPrice;
                      const discountPercentage =
                        product.discountPercentage || 0;
                      const itemTotal =
                        parseFloat(product.itemDiscountedTotal) || 0;

                      return (
                        <tr key={index}>
                          <td className="border px-4 py-2 w-16">{index + 1}</td>
                          <td className="border px-4 py-2 break-words">
                            {product.productName}
                          </td>
                          <td className="border px-4 py-2 w-20 text-center">
                            {product.quantity}
                          </td>
                          <td className="border px-4 py-2 w-24 text-right">
                            {originalPrice.toFixed(2)}
                          </td>
                          <td className="border px-4 py-2 w-24 text-center">
                            {discountPercentage > 0
                              ? `${discountPercentage}%`
                              : "0%"}
                          </td>
                          <td className="border px-4 py-2 w-24 text-right">
                            {discountedPrice.toFixed(2)}
                          </td>
                          <td className="border px-4 py-2 w-24 text-right">
                            {itemTotal.toFixed(2)}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mt-6">
              <div className="w-full md:w-1/3">
                <div className="flex justify-between mb-2">
                  <p className="text-gray-700">Sub Total:</p>
                  <p className="text-gray-700 font-semibold">
                    Rs.{" "}
                    {(invoiceOrderData?.totalAmount
                      ? parseFloat(invoiceOrderData.totalAmount)
                      : (invoiceOrderData?.productInfo || []).reduce(
                          (sum, product) =>
                            sum +
                            (parseFloat(product.itemDiscountedTotal) || 0),
                          0,
                        )
                    ).toFixed(2)}
                  </p>
                </div>
                {invoiceOrderData?.deliveryCharges &&
                  parseFloat(invoiceOrderData.deliveryCharges) > 0 && (
                    <div className="flex justify-between mb-2">
                      <p className="text-gray-700">Shipping Charges:</p>
                      <p className="text-gray-700 font-semibold">
                        Rs.{" "}
                        {parseFloat(invoiceOrderData.deliveryCharges).toFixed(
                          2,
                        )}
                      </p>
                    </div>
                  )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between">
                    <p className="text-lg font-semibold text-green-700">
                      Grand Total:
                    </p>
                    <p className="text-2xl font-bold text-green-700">
                      Rs.{" "}
                      {(
                        (invoiceOrderData?.totalAmount
                          ? parseFloat(invoiceOrderData.totalAmount)
                          : (invoiceOrderData?.productInfo || []).reduce(
                              (sum, product) =>
                                sum +
                                (parseFloat(product.itemDiscountedTotal) || 0),
                              0,
                            )) +
                        (parseFloat(invoiceOrderData?.deliveryCharges) || 0)
                      ).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Order;
